import { gql, useMutation, useQuery } from '@graphcommerce/graphql'
import { useCartIdCreate } from '@graphcommerce/magento-cart'
import { CartItemFragment } from '@graphcommerce/magento-cart-items'
import { ConfigurableProductAddToCartDocument } from '@graphcommerce/magento-product-configurable/graphql/ConfigurableProductAddToCart.gql'
import { gtagAddToCart } from '../GoogleAnalytics/events/gtagAddToCart'
import { SvgIcon, Tooltip } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import {
  ButtonBack,
  ButtonNext,
  CarouselContext,
  CarouselProvider,
  Slide,
  Slider,
} from 'pure-react-carousel'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import missingImageIcon from '../../assets/images/missing-image-icon.svg'
import { formatDaysToDeliveryDisplay } from '../../hooks/displayTools'
import { GenerateSignedCommandUrlDocument } from '../Artwork/GenerateSignedCommandUrl.gql'
import { DeliveryCalendarPopover } from '../Product/DeliveryCalendarPopover/DeliveryCalendarPopover'
import type { ConfigurableOption, Variant } from './VariantSelector'
import { VariantSelector } from './VariantSelector'

function ImageSlideCounter({ total }: { total: number }) {
  const carouselContext = useContext(CarouselContext)
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide)

  useEffect(() => {
    const handler = () => setCurrentSlide(carouselContext.state.currentSlide)
    carouselContext.subscribe(handler)
    return () => carouselContext.unsubscribe(handler)
  }, [carouselContext])

  return (
    <div className='absolute bottom-2 right-3 z-10 rounded-full bg-black/15 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm'>
      {currentSlide + 1} / {total}
    </div>
  )
}

const PRODUCT_OPTIONS_QUERY = gql`
  query PricePointsProductOptions($sku: String!) {
    products(filter: { sku: { eq: $sku } }) {
      items {
        uid
        sku
        __typename
        media_gallery {
          label
          __typename
          ... on ProductImage {
            url
          }
        }
        amfiles {
          file_id
          filename
          label
          url
        }
        ... on SimpleProduct {
          options {
            uid
            __typename
            sort_order
            title
            ... on CustomizableFieldOption {
              color_list {
                code
                label
                default
              }
            }
            ... on CustomizableDropDownOption {
              dropdownValue: value {
                uid
                title
                price
                option_type_id
              }
            }
          }
        }
        ... on ConfigurableProduct {
          fourover_options_modifier {
            optionLabel
            modifier
          }
          options {
            uid
            __typename
            sort_order
            title
            ... on CustomizableFieldOption {
              color_list {
                code
                label
                default
              }
            }
            ... on CustomizableDropDownOption {
              dropdownValue: value {
                uid
                title
                price
                option_type_id
              }
            }
          }
          configurable_options {
            uid
            attribute_code
            label
            position
            values {
              uid
              store_label
              value_index
              swatch_data {
                value
                ... on ColorSwatchData {
                  __typename
                }
                ... on ImageSwatchData {
                  __typename
                }
                ... on TextSwatchData {
                  __typename
                }
              }
            }
          }
          variants {
            attributes {
              code
              uid
              value_index
              label
            }
            product {
              uid
              sku
              stock_status
              price_tiers {
                quantity
                final_price {
                  value
                  currency
                }
              }
            }
          }
        }
      }
    }
  }
`

interface PricePoint {
  quantity: number | string
  price: number | string
  unit?: string
}

interface PricePointsElementProps {
  name: string
  image_url?: string
  url?: string
  production_time?: string
  price_points: PricePoint[]
  compact?: boolean
  sku?: string
  configurable_options?: ConfigurableOption[]
  variants?: Variant[]
  artwork_required?: boolean
}

export function PricePointsElement({
  name,
  image_url,
  url,
  production_time,
  price_points,
  compact = false,
  sku,
  configurable_options,
  variants,
  artwork_required,
}: PricePointsElementProps): JSX.Element {
  const [staticPoints] = useState(() => structuredClone(price_points ?? []))
  const [selectedUids, setSelectedUids] = useState<string[] | null>(null)
  const [selectedPriceIndex, setSelectedPriceIndex] = useState<number | null>(() => {
    if (!price_points?.length) return null
    let minIdx = 0
    let minQty = Number(String(price_points[0].quantity).replace(/,/g, ''))
    for (let i = 1; i < price_points.length; i++) {
      const qty = Number(String(price_points[i].quantity).replace(/,/g, ''))
      if (qty < minQty) {
        minQty = qty
        minIdx = i
      }
    }
    return minIdx
  })
  const [addedToCart, setAddedToCart] = useState(false)
  const [cartError, setCartError] = useState<string | null>(null)
  const [selectedColors, setSelectedColors] = useState<Record<string, string>>({})
  const [dropdownSelections, setDropdownSelections] = useState<Record<string, string>>({})
  const [expandedDropdownAttrs, setExpandedDropdownAttrs] = useState<Set<string>>(new Set())

  const [artworkFile, setArtworkFile] = useState<{
    url: string
    fileName: string
    size: number
  } | null>(null)
  const [proofOption, setProofOption] = useState<'NO_PROOF_NEEDED' | 'PROOF_NEEDED'>('PROOF_NEEDED')
  const [uploadingArtwork, setUploadingArtwork] = useState(false)
  const [artworkError, setArtworkError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selectedQtyRef = useRef<number | null>(
    price_points?.length
      ? Math.min(...price_points.map((p) => Number(String(p.quantity).replace(/,/g, ''))))
      : null,
  )

  const { data: productData } = useQuery(PRODUCT_OPTIONS_QUERY, {
    variables: { sku },
    skip: !sku,
    fetchPolicy: 'cache-first',
  })

  const productItem = productData?.products?.items?.[0]

  const fouroverModifiers: Array<{ optionLabel: string; modifier: string }> =
    productItem?.fourover_options_modifier ?? []

  // CustomizableFieldOption color options (e.g. "Bag Color", "Foil Stamp Color")
  const colorFieldOptions: Array<{
    uid: string
    title: string
    sort_order?: number
    color_list: Array<{ code: string; label: string; default?: boolean }>
  }> = (productItem?.options ?? [])
    .filter(
      (o: any) =>
        o.__typename === 'CustomizableFieldOption' &&
        Array.isArray(o.color_list) &&
        o.color_list.length > 0,
    )
    .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

  // CustomizableDropDownOption options (e.g. TAT, Colorspec, etc.)
  const dropdownFieldOptions: Array<{
    uid: string
    title: string
    sort_order?: number
    dropdownValue: Array<{ uid: string; title: string; price?: number; option_type_id?: number }>
  }> = (productItem?.options ?? [])
    .filter((o: any) => {
      if (o.__typename !== 'CustomizableDropDownOption') return false
      if (!Array.isArray(o.dropdownValue) || o.dropdownValue.length === 0) return false
      const mod = fouroverModifiers.find((m) => m.optionLabel === o.title)?.modifier
      if (mod === 'Disable') return false
      if (mod === 'DisableIfSingle' && o.dropdownValue.length < 2) return false
      return true
    })
    .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

  const amfiles: Array<{ file_id: number; filename: string; label: string; url: string }> = (
    productItem?.amfiles ?? []
  ).filter((f: any) => f?.url && f?.label)

  // Auto-select default color for each field option on product load
  useEffect(() => {
    if (!colorFieldOptions.length) return
    setSelectedColors((prev) => {
      const next = { ...prev }
      for (const opt of colorFieldOptions) {
        if (!next[opt.uid]) {
          const def = opt.color_list.find((c) => c.default) ?? opt.color_list[0]
          if (def?.code) next[opt.uid] = def.code
        }
      }
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productItem?.uid])

  // Auto-select first dropdown value for each dropdown option on product load
  useEffect(() => {
    if (!dropdownFieldOptions.length) return
    setDropdownSelections((prev) => {
      const next = { ...prev }
      for (const opt of dropdownFieldOptions) {
        if (!next[opt.uid] && opt.dropdownValue[0]?.uid) {
          next[opt.uid] = String(opt.dropdownValue[0].uid)
        }
      }
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productItem?.uid])

  // All product images from media_gallery, falling back to AI-provided single URL
  const mediaGallery: Array<{ url: string; label?: string | null }> = (
    productItem?.media_gallery ?? []
  ).filter((m: { url?: unknown }) => m?.url)
  let imageUrls: string[]
  if (mediaGallery.length > 0) {
    imageUrls = mediaGallery.map((m) => m.url)
  } else if (image_url) {
    imageUrls = [image_url]
  } else {
    imageUrls = []
  }

  // Use freshly fetched configurable data (better swatch_data) if available, fall back to AI-provided
  const effectiveConfigurableOptions: ConfigurableOption[] =
    productItem?.configurable_options ?? configurable_options ?? []
  const effectiveVariants: Variant[] = productItem?.variants ?? variants ?? []

  const activeVariant = useMemo(() => {
    if (!selectedUids || !effectiveVariants.length) return null
    return (
      effectiveVariants.find((v) =>
        selectedUids.every((uid) => v.attributes?.some((a: any) => a.uid === uid)),
      ) ?? null
    )
  }, [selectedUids, effectiveVariants])

  const currentPricePoints: PricePoint[] = useMemo(() => {
    const toRounded = (raw: number) => parseFloat(raw.toFixed(raw >= 1 ? 2 : 3))
    const tierToPoint = (tier: any) => ({
      quantity: tier.quantity ?? 0,
      price: toRounded(tier.final_price?.value ?? 0),
    })
    const tiers = (activeVariant as any)?.product?.price_tiers
    if (tiers?.length) return tiers.map(tierToPoint)
    const firstVariantTiers = (effectiveVariants[0] as any)?.product?.price_tiers
    if (firstVariantTiers?.length) return firstVariantTiers.map(tierToPoint)
    return staticPoints
  }, [activeVariant, effectiveVariants, staticPoints])

  useEffect(() => {
    if (!activeVariant) return
    const tiers = (activeVariant as any)?.product?.price_tiers
    if (!tiers?.length) return

    const prevQty = selectedQtyRef.current
    if (prevQty !== null) {
      const matchIdx = tiers.findIndex((t: any) => (t?.quantity ?? 0) === prevQty)
      if (matchIdx !== -1) {
        setSelectedPriceIndex(matchIdx)
        return
      }
    }

    let minIdx = 0
    let minQty: number = tiers[0]?.quantity ?? 0
    for (let i = 1; i < tiers.length; i++) {
      const qty: number = tiers[i]?.quantity ?? 0
      if (qty < minQty) {
        minQty = qty
        minIdx = i
      }
    }
    selectedQtyRef.current = minQty
    setSelectedPriceIndex(minIdx)
  }, [activeVariant])

  const hasConfigurableOptions =
    effectiveConfigurableOptions.length > 0 && effectiveVariants.length > 0
  const hasColorFieldOptions = colorFieldOptions.length > 0
  const hasDropdownOptions = dropdownFieldOptions.length > 0

  const isConfigurable =
    !!sku && (hasConfigurableOptions || hasColorFieldOptions || hasDropdownOptions)

  // Ready to add when all axes are selected
  const allConfigurableSelected = !hasConfigurableOptions || selectedUids !== null
  const allColorsSelected = colorFieldOptions.every((opt) => !!selectedColors[opt.uid])
  const allDropdownsSelected = dropdownFieldOptions.every((opt) => !!dropdownSelections[opt.uid])
  const isReadyToAddToCart = allConfigurableSelected && allColorsSelected && allDropdownsSelected

  const [mutate, { loading: cartLoading }] = useMutation(ConfigurableProductAddToCartDocument)
  const [generateSignedUrl] = useMutation(GenerateSignedCommandUrlDocument)
  const getOrCreateCartId = useCartIdCreate()

  const handleArtworkUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (file.size > 20 * 1024 * 1024) {
      setArtworkError('File must be under 20MB')
      return
    }
    setArtworkError(null)
    setUploadingArtwork(true)
    try {
      const s3Key = `artworks/${Date.now()}-${file.name}`
      const { data } = await generateSignedUrl({
        variables: { objectKey: s3Key, bucket: 'artwork' },
      })
      const signedUrl = data?.generateSignedCommandUrl?.url
      if (!signedUrl) throw new Error('Failed to get upload URL')
      await fetch(signedUrl, { method: 'PUT', body: file })
      const cleanUrl = signedUrl.substring(0, signedUrl.indexOf('?'))
      setArtworkFile({ url: cleanUrl, fileName: file.name, size: file.size })
    } catch {
      setArtworkError('Upload failed. Please try again.')
    } finally {
      setUploadingArtwork(false)
    }
  }

  const handleAddToCart = async () => {
    if (!sku) return
    setCartError(null)
    const quantity =
      selectedPriceIndex !== null
        ? Number(String(currentPricePoints[selectedPriceIndex].quantity).replace(/,/g, ''))
        : 1
    const enteredOptions = [
      ...colorFieldOptions.map((opt) => ({ uid: opt.uid, value: selectedColors[opt.uid] ?? '' })),
      ...dropdownFieldOptions.map((opt) => ({
        uid: opt.uid,
        value: dropdownSelections[opt.uid] ?? '',
      })),
    ].filter((o) => o.value)
    try {
      const cartId = await getOrCreateCartId()
      const result = await mutate({
        variables: {
          cartId,
          sku,
          quantity,
          selectedOptions: selectedUids ?? [],
          enteredOptions,
        },
      })
      const userErrors = result.data?.addProductsToCart?.user_errors ?? []
      if (userErrors.length > 0) {
        setCartError(userErrors[0]?.message ?? 'Could not add to cart')
      } else {
        const addedItem = result.data?.addProductsToCart?.cart.items?.slice(-1)[0]
        gtagAddToCart(addedItem as unknown as CartItemFragment, 1, { added_from: 'ai_chat' })
        setAddedToCart(true)
      }
    } catch {
      setCartError('Could not add to cart. Please try again.')
    }
  }

  // Slider shown in all modes. Buttons hidden on mobile and in popup/sidebar (compact=false).
  const slides = imageUrls.length > 0 ? imageUrls : [image_url ?? '']
  // In chat (compact) show 5:3; in popup/sidebar show a taller 4:3
  const slideRatio = compact ? '5/3' : '4/3'
  const showButtons = slides.length > 1
  const imageBlock = (
    <div className='relative w-full overflow-hidden rounded-t-[22px]'>
      <CarouselProvider
        key={sku ?? name}
        naturalSlideWidth={5}
        naturalSlideHeight={3}
        totalSlides={slides.length}
        visibleSlides={1}
        infinite={showButtons}
        isIntrinsicHeight
        dragEnabled={showButtons}
        touchEnabled={showButtons}
      >
        <Slider>
          {slides.map((imgUrl, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Slide key={i} index={i}>
              <div className='relative w-full' style={{ aspectRatio: slideRatio }}>
                <Image
                  src={imgUrl || missingImageIcon}
                  alt={`${name} — image ${i + 1}`}
                  fill
                  className='object-cover'
                  unoptimized
                />
              </div>
            </Slide>
          ))}
        </Slider>
        {showButtons && (
          <>
            {/* Buttons: only in chat mode on md+ screens */}
            <ButtonBack
              className={`absolute left-2 top-1/2 z-10 h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all hover:bg-white/35 disabled:opacity-30 active:scale-95 ${compact ? 'hidden md:flex' : 'hidden'}`}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M15 18l-6-6 6-6' />
              </svg>
            </ButtonBack>
            <ButtonNext
              className={`absolute right-2 top-1/2 z-10 h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all hover:bg-white/35 disabled:opacity-30 active:scale-95 ${compact ? 'hidden md:flex' : 'hidden'}`}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M9 18l6-6-6-6' />
              </svg>
            </ButtonNext>
            {/* Counter: only in chat mode on md+ screens */}
            <div className={compact ? 'hidden md:block' : 'hidden'}>
              <ImageSlideCounter total={slides.length} />
            </div>
          </>
        )}
      </CarouselProvider>
    </div>
  )

  return (
    <div className='my-3 overflow-hidden rounded-[22px] bg-white/80 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
      {imageBlock}

      <div className='px-5 pt-4'>
        <div className='flex items-baseline gap-3'>
          <h1 className='Type-Large-Medium md:Type-XL-Medium text-black'>{name}</h1>
          {url && (
            <Link href={url} className='shrink-0 text-[10px] text-[#888] underline'>
              click here to navigate to product page
            </Link>
          )}
        </div>
        {production_time && (
          <div className='mt-0.5 flex items-center gap-1.5'>
            <span className='text-[13px] leading-snug text-[#6C6C70]'>
              Est. Delivery: {formatDaysToDeliveryDisplay(production_time)}
            </span>
            <DeliveryCalendarPopover production_time={Number(production_time)} />
            <Tooltip
              title='Estimated delivery dates are not guaranteed delivery dates. It assumes your proof is approved within 24 hours. Rush services may be available. We will make every effort possible to deliver by this date.'
              arrow
            >
              <SvgIcon sx={{ fontSize: 16, color: '#888', cursor: 'pointer' }}>
                <path d='M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z' />
              </SvgIcon>
            </Tooltip>
          </div>
        )}
        {isConfigurable && (
          <p className='mt-4 text-[13px] leading-snug text-[#6C6C70]'>
            Set up your product below — choose your options, pick a quantity, and add it to your
            cart. If artwork is needed, we've got you covered with templates or hands-on support
            from our team.
          </p>
        )}
        {amfiles.length > 0 && (
          <div className='mt-3'>
            <p className='mb-2 text-[13px] font-medium text-[#6C6C70]'>Print Templates</p>
            <div className={compact ? 'flex flex-wrap gap-2' : 'flex flex-col gap-2'}>
              {amfiles.map((file) => (
                <a
                  key={file.file_id}
                  href={file.url}
                  target='_blank'
                  rel='noreferrer'
                  className={`rounded-[8px] border border-dashed border-[rgba(60,60,67,0.3)] px-3 py-1.5 text-[12px] font-medium text-[#5D54DE] transition-colors hover:border-[#5D54DE] hover:bg-[rgba(93,84,222,0.05)] ${compact ? '' : 'w-full text-center'}`}
                >
                  {file.label || file.filename}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {isConfigurable && (
        <div className='px-5 pb-2 pt-3'>
          {hasConfigurableOptions && (
            <VariantSelector
              configurable_options={effectiveConfigurableOptions}
              variants={effectiveVariants}
              onSelectionChange={setSelectedUids}
              compact={compact}
            />
          )}

          {colorFieldOptions.map((opt) => {
            const selectedColorLabel = opt.color_list.find(
              (c) => c?.code === selectedColors[opt.uid],
            )?.label
            return (
              <div key={opt.uid} className='mb-6'>
                <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
                  {opt.title}
                  {selectedColorLabel && (
                    <span className='font-medium text-black'>{` = ${selectedColorLabel.toUpperCase()}`}</span>
                  )}
                </p>
                <div className='grid grid-cols-4 gap-x-2 gap-y-5'>
                  {opt.color_list.filter(Boolean).map((color) => {
                    const isSelected = selectedColors[opt.uid] === color.code
                    return (
                      <Tooltip key={color.code} title={color.label ?? ''} placement='top' arrow>
                        <div className='relative'>
                          <button
                            type='button'
                            aria-label={color.label ?? ''}
                            onClick={() =>
                              setSelectedColors((prev) => ({ ...prev, [opt.uid]: color.code }))
                            }
                            className={`h-9 w-full rounded-[6px] transition-all active:scale-[0.95] ${
                              isSelected
                                ? 'ring-2 ring-[#5D54DE] ring-offset-2'
                                : 'ring-1 ring-[#d9d9d9] hover:ring-[#afafaf]'
                            }`}
                            style={{ backgroundColor: color.code ?? '#ccc' }}
                          />
                          {isSelected && (
                            <span className='absolute left-1/2 top-full -translate-x-1/2 pt-0.5 whitespace-nowrap text-center text-[9px] font-medium leading-tight text-black'>
                              {color.label}
                            </span>
                          )}
                        </div>
                      </Tooltip>
                    )
                  })}
                </div>
              </div>
            )
          })}

          {dropdownFieldOptions.map((opt) => {
            const selectedVal = dropdownSelections[opt.uid]
            const values = opt.dropdownValue ?? []
            const isThreeColTrigger =
              compact && (values.length > 12 || opt.title.toLowerCase().includes('size'))
            const isExpanded = expandedDropdownAttrs.has(opt.uid)
            let gridClass: string
            if (!compact) {
              gridClass = 'grid grid-cols-1 gap-2'
            } else if (isThreeColTrigger) {
              gridClass = 'grid grid-cols-1 gap-2 lg:grid-cols-3'
            } else {
              gridClass = 'grid grid-cols-1 gap-2 lg:grid-cols-2'
            }
            return (
              <div key={opt.uid} className='mb-6'>
                <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
                  {opt.title}
                </p>
                <div className={gridClass}>
                  {values.map((option: any, idx: number) => {
                    const isSelected = selectedVal === option.uid?.toString()
                    const hiddenOnMobile = isThreeColTrigger && !isExpanded && idx >= 8
                    return (
                      <div
                        key={option.uid}
                        className={hiddenOnMobile ? 'hidden lg:block' : undefined}
                      >
                        <button
                          type='button'
                          onClick={() =>
                            setDropdownSelections((prev) => ({
                              ...prev,
                              [opt.uid]: option.uid?.toString(),
                            }))
                          }
                          className={`w-full overflow-hidden rounded-[4px] px-2.5 py-2.5 transition-all active:scale-[0.97] ${
                            isSelected
                              ? 'bg-[#D1CBCB33] border-[1.6px] border-[#5D54DE] text-black shadow-[0px_2px_3px_0px_rgba(0,0,0,0.25)]'
                              : 'bg-[#F8F8F8] border-[1.4px] border-[#908B8B] text-black'
                          }`}
                        >
                          <span
                            className={`block truncate text-[13px] uppercase tracking-[0.04em] ${isSelected ? 'font-bold' : 'font-medium'}`}
                          >
                            {option.title}
                          </span>
                        </button>
                      </div>
                    )
                  })}
                </div>
                {isThreeColTrigger && values.length > 8 && (
                  <button
                    type='button'
                    className='mt-2 text-[11px] font-medium text-[#5D54DE] lg:hidden'
                    onClick={() =>
                      setExpandedDropdownAttrs((prev) => {
                        const next = new Set(prev)
                        if (isExpanded) next.delete(opt.uid)
                        else next.add(opt.uid)
                        return next
                      })
                    }
                  >
                    {isExpanded ? 'Show fewer options' : `${values.length - 8} more options`}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {currentPricePoints.length > 0 && (
        <div className='px-5 pb-2 pt-3'>
          <p className='mb-2.5 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
            Pricing
          </p>
          <div className='rounded-[14px] bg-[rgba(120,120,128,0.08)] p-2'>
            {currentPricePoints.map((point, i) => {
              const isSelected = selectedPriceIndex === i
              const qty =
                Number(String(point.quantity).replace(/,/g, '')).toLocaleString() || point.quantity
              const priceNum = Number(point.price)
              const decimals = priceNum >= 1 ? 2 : 3
              const priceStr = priceNum.toLocaleString('en-US', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
              })
              const roundedUnit = parseFloat(priceNum.toFixed(decimals))
              const total = (
                roundedUnit * Number(String(point.quantity).replace(/,/g, ''))
              ).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              return (
                <button
                  key={i}
                  type='button'
                  onClick={() => {
                    if (isSelected) {
                      selectedQtyRef.current = null
                      setSelectedPriceIndex(null)
                    } else {
                      selectedQtyRef.current = Number(String(point.quantity).replace(/,/g, ''))
                      setSelectedPriceIndex(i)
                    }
                  }}
                  className={`flex w-full items-center rounded-[4px] px-4 py-3.5 transition-all active:scale-[0.99] ${
                    isSelected
                      ? 'bg-[#D1CBCB33] shadow-[0px_2px_3px_0px_rgba(0,0,0,0.25)] ring-[1.6px] ring-inset ring-[#5D54DE]'
                      : ''
                  }`}
                >
                  <span
                    className={`flex-1 text-left text-[15px] text-[#1C1C1E] ${isSelected ? 'font-bold' : 'font-medium'}`}
                  >
                    {qty}
                  </span>
                  <span
                    className={`flex-1 text-center text-[15px] text-[#1C1C1E] ${isSelected ? 'font-bold' : ''}`}
                  >
                    ${priceStr} ea
                  </span>
                  <span
                    className={`flex-1 text-right text-[15px] text-[#1C1C1E] ${isSelected ? 'font-bold' : ''}`}
                  >
                    ${total}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {isConfigurable && (
        <div className='px-5 pb-5 pt-3'>
          {addedToCart ? (
            <div className='flex items-center gap-3 overflow-hidden rounded-49xl bg-white/80 px-4 py-3.5 shadow-[0_2px_16px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
              <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#34C759]'>
                <svg className='h-3.5 w-3.5 text-white' fill='currentColor' viewBox='0 0 20 20'>
                  <path
                    fillRule='evenodd'
                    d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <p className='text-[15px] text-black'>
                {selectedPriceIndex !== null
                  ? `${Number(String(currentPricePoints[selectedPriceIndex].quantity).replace(/,/g, '')).toLocaleString()} units added to your cart!`
                  : 'Added to your cart!'}
              </p>
            </div>
          ) : (
            <>
              {artwork_required && (
                <div className='mb-3'>
                  <p className='mb-2.5 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
                    Artwork
                  </p>
                  {artworkFile ? (
                    <div className='overflow-hidden rounded-[14px] bg-[rgba(120,120,128,0.08)] p-4'>
                      <div className='mb-3 flex items-center gap-3'>
                        {artworkFile.fileName.toLowerCase().endsWith('.pdf') ? (
                          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-34xl bg-red-100'>
                            <span className='text-[10px] font-bold text-red-600'>PDF</span>
                          </div>
                        ) : (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={artworkFile.url}
                            alt={artworkFile.fileName}
                            className='h-12 w-12 shrink-0 rounded-34xl object-cover'
                          />
                        )}
                        <div className='min-w-0 flex-1'>
                          <p className='truncate text-[13px] font-medium text-black'>
                            {artworkFile.fileName}
                          </p>
                          <p className='text-[11px] text-[#8E8E93]'>
                            {Math.round(artworkFile.size / 1024)}kb
                          </p>
                        </div>
                        <button
                          type='button'
                          onClick={() => {
                            setArtworkFile(null)
                            if (fileInputRef.current) fileInputRef.current.value = ''
                          }}
                          className='text-[13px] text-[#8E8E93] transition-colors hover:text-black'
                        >
                          Change
                        </button>
                      </div>
                      <div className='space-y-1 border-t border-[rgba(60,60,67,0.12)] pt-3'>
                        {(['PROOF_NEEDED', 'NO_PROOF_NEEDED'] as const).map((opt) => (
                          <button
                            key={opt}
                            type='button'
                            onClick={() => setProofOption(opt)}
                            className={`flex w-full items-center gap-2.5 rounded-35xl px-3 py-2.5 text-left transition-all ${proofOption === opt ? 'bg-[rgba(0,0,0,0.07)]' : ''}`}
                          >
                            <div
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${proofOption === opt ? 'border-black' : 'border-[#C7C7CC]'}`}
                            >
                              {proofOption === opt && (
                                <div className='h-2 w-2 rounded-full bg-black' />
                              )}
                            </div>
                            <div>
                              <p className='text-[13px] font-medium text-black'>
                                {opt === 'PROOF_NEEDED' ? 'Send proof first' : 'Print ASAP'}
                              </p>
                              <p className='text-[11px] text-[#8E8E93]'>
                                {opt === 'PROOF_NEEDED'
                                  ? "We'll email you a proof before printing"
                                  : 'No proof needed, print immediately'}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      {artworkError && (
                        <p className='mb-2 text-[13px] text-[#FF3B30]'>{artworkError}</p>
                      )}
                      <button
                        type='button'
                        disabled={uploadingArtwork}
                        onClick={() => fileInputRef.current?.click()}
                        className='w-full rounded-[14px] border-2 border-dashed border-[rgba(60,60,67,0.2)] py-4 text-[15px] font-medium text-[#8E8E93] transition-all hover:border-[rgba(60,60,67,0.4)] hover:text-black active:scale-[0.99] disabled:opacity-40'
                      >
                        {uploadingArtwork ? 'Uploading…' : '+ Upload Artwork'}
                      </button>
                    </>
                  )}
                  <input
                    type='file'
                    ref={fileInputRef}
                    accept='.pdf,.ai,.svg,.jpg,.jpeg,.png'
                    className='hidden'
                    onChange={handleArtworkUpload}
                  />
                </div>
              )}
              {cartError && <p className='mb-2 text-[13px] text-[#FF3B30]'>{cartError}</p>}
              <button
                type='button'
                disabled={!isReadyToAddToCart || cartLoading || (artwork_required && !artworkFile)}
                onClick={handleAddToCart}
                className='w-full rounded-[14px] bg-[rgba(0,0,0,0.07)] py-3.5 text-[15px] font-semibold text-black transition-all active:scale-[0.98] disabled:opacity-40'
              >
                {cartLoading
                  ? 'Adding…'
                  : selectedPriceIndex !== null
                    ? `Add ${Number(String(currentPricePoints[selectedPriceIndex].quantity).replace(/,/g, '')).toLocaleString()} to Cart`
                    : 'Add to Cart'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
